import type { GetAggregatedFormDataParams } from '#services/api/insights.ts';
import type { loader as homeLoader } from '../Home.data.ts';

import { endOfDay, format, startOfDay } from 'date-fns';
import { useLoaderData, useSearchParams } from 'react-router';

import { apiTimezone, FORMATS } from '#utils/dates';

const searchParamsWithMultipleValues = [
  { id: 'pId', name: 'providerIds' },
  { id: 'astId', name: 'assessmentTypeIds' },
] as const;

export function useFormWidget(source?: 'form-completion-by-type' | 'forms-completed') {
  const homeData = useLoaderData<ReturnType<typeof homeLoader>>();

  const [routerSearchParams] = useSearchParams();

  function getCommonRequestParams() {
    return {
      timezone: apiTimezone,
      minCreated: format(startOfDay(homeData.from), FORMATS.MYSQL_DATETIME),
      maxCreated: format(endOfDay(homeData.to), FORMATS.MYSQL_DATETIME),
      providerId: homeData.providerIds,
      assessmentTypeId: homeData.assessmentTypeIds,
      assessmentTypeRequired:
        homeData.formsPriority === 'all'
          ? undefined
          : homeData.formsPriority === 'required'
            ? 1
            : 0,
    } as const satisfies GetAggregatedFormDataParams;
  }

  function getDrilldownRoutePath(
    getSearchParams?: (currentSearchParams: URLSearchParams) => URLSearchParams
  ) {
    const searchParams = new URLSearchParams();

    // Avoid passing date params if they are not set to help in the drilldown
    // filters comparison by not having a mismatch of the keys with the same
    // internal values.
    if (routerSearchParams.get('from')) {
      searchParams.set('from', homeData.fromDateFormat);
    }
    if (routerSearchParams.get('to')) {
      searchParams.set('to', homeData.toDateFormat);
    }

    for (const searchParamWithMultipleValues of searchParamsWithMultipleValues) {
      for (const value of homeData[searchParamWithMultipleValues.name]) {
        searchParams.append(searchParamWithMultipleValues.id, String(value));
      }
    }

    if (homeData.formsPriority !== 'all') {
      searchParams.set('fp', homeData.formsPriority);
    }

    if (source) {
      searchParams.set('source', source);
    }

    searchParams.set('tab', 'forms');

    const finalSearchParams = getSearchParams?.(searchParams) ?? searchParams;

    return `/forms/drilldown?${finalSearchParams.toString()}` as const;
  }

  return {
    getCommonRequestParams,
    getDrilldownRoutePath,
    homeLoaderData: homeData,
  };
}
